import type { FormQuestionSchema } from '../../api/formSampling';
import {
  isSerializedRangeValues,
  parseFormAnswerFileRef,
  parseRangeAnswerValues,
  parseRangeColumnValue,
} from '../../api/formSampling';
import { strapiMediaUrl } from '../../api/strapi';
import type {
  LaboratoryResultSamplingQuestion,
  LaboratoryResultSamplingSnapshot,
} from '../../api/strapiCrm';
import {
  findQuestionOptionForColumn,
  groupQuestionsForRender,
  type MatrixColumn,
} from '../SurveyPage/components/surveyFormQuestionMatrix';

export type SnapshotRenderBlock =
  | { kind: 'linear'; question: LaboratoryResultSamplingQuestion }
  | { kind: 'matrix'; questions: LaboratoryResultSamplingQuestion[]; columns: MatrixColumn[] };

function rangeBoundsFromJson(raw: string): { start: number; end: number } | null {
  if (!isSerializedRangeValues(raw)) return null;
  const keys = Object.keys(parseRangeAnswerValues(raw))
    .map((k) => Number(k))
    .filter((n) => Number.isInteger(n))
    .sort((a, b) => a - b);
  if (!keys.length) return null;
  return { start: keys[0]!, end: keys[keys.length - 1]! };
}

function uniqueOptionsFromValues(question: LaboratoryResultSamplingQuestion): FormQuestionSchema['options'] {
  const seen = new Set<string>();
  const options: FormQuestionSchema['options'] = [];
  for (const cell of question.option_values) {
    const id = cell.option_document_id.trim();
    const title = cell.option_title.trim();
    const key = id || title.toLocaleLowerCase('id');
    if (!key || seen.has(key)) continue;
    seen.add(key);
    const inferred = rangeBoundsFromJson(cell.value);
    options.push({
      documentId: id || key,
      title: title || id,
      answer_type: inferred ? 'range' : question.answer_type || 'text',
      sort_order: options.length,
      start: inferred?.start ?? null,
      end: inferred?.end ?? null,
    });
  }
  return options;
}

export function snapshotQuestionToSchema(question: LaboratoryResultSamplingQuestion): FormQuestionSchema {
  const fromMaster = question.options.map((opt) => {
    const valueRow = question.option_values.find((cell) => cell.option_document_id === opt.document_id);
    const inferred = valueRow ? rangeBoundsFromJson(valueRow.value) : null;
    const answerType = String(opt.answer_type ?? '').trim() || (inferred ? 'range' : question.answer_type || 'text');
    return {
      documentId: opt.document_id,
      title: opt.title,
      answer_type: answerType,
      sort_order: opt.sort_order,
      start: opt.start ?? inferred?.start ?? null,
      end: opt.end ?? inferred?.end ?? null,
    };
  });
  const options = fromMaster.length ? fromMaster : uniqueOptionsFromValues(question);
  return {
    documentId: question.document_id,
    form_question_name: '',
    title: question.title,
    answer_type: question.answer_type || 'text',
    has_option: options.length > 0,
    required: false,
    sort_order: question.sort_order,
    options,
  };
}

export function groupSnapshotQuestionsForRender(
  questions: LaboratoryResultSamplingQuestion[],
): SnapshotRenderBlock[] {
  const byId = new Map(questions.map((q) => [q.document_id, q]));
  return groupQuestionsForRender(questions.map(snapshotQuestionToSchema)).map((block) => {
    if (block.kind === 'linear') {
      return { kind: 'linear', question: byId.get(block.question.documentId) ?? questions[0]! };
    }
    return {
      kind: 'matrix',
      questions: block.questions
        .map((schema) => byId.get(schema.documentId))
        .filter((q): q is LaboratoryResultSamplingQuestion => q != null),
      columns: block.columns,
    };
  });
}

export function formatSnapshotDateValue(raw: string): string {
  const v = raw.trim();
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v);
  if (!m) return v;
  return `${m[3]}/${m[2]}/${m[1]}`;
}

export function formatSnapshotAnswerValue(raw: string, answerType = ''): string {
  const v = raw.trim();
  if (!v) return '';
  const file = parseFormAnswerFileRef(v);
  if (file) return file.name;
  const type = answerType.trim().toLowerCase();
  if (type === 'date') return formatSnapshotDateValue(v);
  if (type === 'boolean' || type === 'single') {
    if (v === 'true' || v === '1') return 'Ya';
    if (v === 'false' || v === '0') return '';
  }
  if (v === 'true') return 'Ya';
  if (v === 'false') return '';
  return v;
}

export function snapshotQuestionCellValue(
  question: LaboratoryResultSamplingQuestion,
  columnKey: string,
): string {
  const schema = snapshotQuestionToSchema(question);
  const option = findQuestionOptionForColumn(schema, columnKey);
  const rangeValue = parseRangeColumnValue(columnKey);
  if (option) {
    const byId = question.option_values.find((cell) => cell.option_document_id === option.documentId);
    if (byId) {
      if (rangeValue != null && isSerializedRangeValues(byId.value)) {
        const parsed = parseRangeAnswerValues(byId.value);
        return formatSnapshotAnswerValue(parsed[String(rangeValue)] ?? '', question.answer_type);
      }
      return formatSnapshotAnswerValue(byId.value, option.answer_type || question.answer_type);
    }
    const byTitle = question.option_values.find(
      (cell) => cell.option_title.trim().toLocaleLowerCase('id') === option.title.trim().toLocaleLowerCase('id'),
    );
    if (byTitle) {
      if (rangeValue != null && isSerializedRangeValues(byTitle.value)) {
        const parsed = parseRangeAnswerValues(byTitle.value);
        return formatSnapshotAnswerValue(parsed[String(rangeValue)] ?? '', question.answer_type);
      }
      return formatSnapshotAnswerValue(byTitle.value, option.answer_type || question.answer_type);
    }
  }
  return '';
}

export function samplingFotoHref(raw?: string | null): string {
  return strapiMediaUrl(String(raw ?? '').trim());
}

export function hasLaboratorySamplingSnapshot(
  sampling?: LaboratoryResultSamplingSnapshot,
): boolean {
  if (!sampling) return false;
  const hasMeta =
    Boolean(sampling.start_time) ||
    Boolean(sampling.end_time) ||
    Boolean(sampling.sample_collection_datetime) ||
    Boolean(sampling.jumlah_sampel) ||
    Boolean(sampling.abnormalitas_note) ||
    Boolean(samplingFotoHref(sampling.foto_url));
  const hasMeasurements = sampling.field_measurements.length > 0;
  const hasAnswers = sampling.form_answer_groups.some((g) => (g.questions?.length ?? 0) > 0);
  return hasMeta || hasMeasurements || hasAnswers;
}
